import type { TimetableStats } from '../../types/timetable';

export interface VersionHistoryEntry {
  id: string;
  timetableId: string;
  version: number;
  name: string;
  status: 'draft' | 'in-progress' | 'generated' | 'finalized' | 'archived';
  createdAt: string;
  createdBy: string;
  createdByName: string;
  notes?: string;
  stats: TimetableStats;
  changesFromPrevious?: {
    lessonsAdded: number;
    lessonsRemoved: number;
    lessonsModified: number;
    conflictDelta: number;
  };
  tags: string[];
  canRestore: boolean;
  canCompare: boolean;
}

export interface ComparisonResult {
  versionA: VersionHistoryEntry;
  versionB: VersionHistoryEntry;
  differences: {
    category: string;
    metricName: string;
    valueA: number | string;
    valueB: number | string;
    change: 'improved' | 'worsened' | 'unchanged';
    delta?: number;
  }[];
  summary: {
    overallBetter: 'A' | 'B' | 'equal';
    recommendation: string;
  };
}

export const mockVersionHistory: VersionHistoryEntry[] = [
  {
    id: 'version-001',
    timetableId: 'timetable-current',
    version: 3,
    name: 'Autumn Term 2025 - Draft 3',
    status: 'generated',
    createdAt: '2025-01-16T14:22:00Z',
    createdBy: 'admin-1',
    createdByName: 'John Smith',
    notes: 'Resolved teacher double-booking conflicts. Improved room utilization.',
    stats: {
      totalLessons: 248,
      conflictCount: 8,
      ruleViolations: { hard: 0, soft: 12 },
      curriculumCoverage: 98.5,
      teacherLoad: [],
      roomUtilization: [],
      teacherPreferenceSatisfaction: 87,
      distributionScore: 92,
    },
    changesFromPrevious: {
      lessonsAdded: 5,
      lessonsRemoved: 2,
      lessonsModified: 18,
      conflictDelta: -7,
    },
    tags: ['current', 'recommended'],
    canRestore: false,
    canCompare: true,
  },
  {
    id: 'version-002',
    timetableId: 'timetable-previous',
    version: 2,
    name: 'Autumn Term 2025 - Draft 2',
    status: 'draft',
    createdAt: '2025-01-14T16:45:00Z',
    createdBy: 'admin-1',
    createdByName: 'John Smith',
    notes: 'Initial generation with new Year 12 options.',
    stats: {
      totalLessons: 245,
      conflictCount: 15,
      ruleViolations: { hard: 0, soft: 18 },
      curriculumCoverage: 95.2,
      teacherLoad: [],
      roomUtilization: [],
      teacherPreferenceSatisfaction: 82,
      distributionScore: 85,
    },
    changesFromPrevious: {
      lessonsAdded: 12,
      lessonsRemoved: 0,
      lessonsModified: 8,
      conflictDelta: 3,
    },
    tags: [],
    canRestore: true,
    canCompare: true,
  },
  {
    id: 'version-003',
    timetableId: 'timetable-v1',
    version: 1,
    name: 'Autumn Term 2025 - Draft 1',
    status: 'archived',
    createdAt: '2025-01-12T09:00:00Z',
    createdBy: 'admin-2',
    createdByName: 'Sarah Wilson',
    notes: 'First draft based on previous year structure.',
    stats: {
      totalLessons: 233,
      conflictCount: 12,
      ruleViolations: { hard: 2, soft: 22 },
      curriculumCoverage: 92.0,
      teacherLoad: [],
      roomUtilization: [],
      teacherPreferenceSatisfaction: 78,
      distributionScore: 80,
    },
    tags: ['initial'],
    canRestore: true,
    canCompare: true,
  },
  {
    id: 'version-004',
    timetableId: 'timetable-2024',
    version: 5,
    name: 'Autumn Term 2024 - Finalized',
    status: 'finalized',
    createdAt: '2024-09-15T12:00:00Z',
    createdBy: 'admin-1',
    createdByName: 'John Smith',
    notes: 'Final version approved by SLT. All year groups finalized.',
    stats: {
      totalLessons: 238,
      conflictCount: 0,
      ruleViolations: { hard: 0, soft: 5 },
      curriculumCoverage: 100,
      teacherLoad: [],
      roomUtilization: [],
      teacherPreferenceSatisfaction: 95,
      distributionScore: 94,
    },
    tags: ['finalized', 'approved'],
    canRestore: false,
    canCompare: true,
  },
  {
    id: 'version-005',
    timetableId: 'timetable-2024-v4',
    version: 4,
    name: 'Autumn Term 2024 - Draft 4',
    status: 'archived',
    createdAt: '2024-09-10T14:30:00Z',
    createdBy: 'admin-1',
    createdByName: 'John Smith',
    notes: 'Pre-finalization review version.',
    stats: {
      totalLessons: 238,
      conflictCount: 2,
      ruleViolations: { hard: 0, soft: 8 },
      curriculumCoverage: 99.5,
      teacherLoad: [],
      roomUtilization: [],
      teacherPreferenceSatisfaction: 92,
      distributionScore: 91,
    },
    tags: [],
    canRestore: false,
    canCompare: true,
  },
];

export const statusConfig: Record<string, { color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; label: string }> = {
  draft: { color: 'default', label: 'Draft' },
  'in-progress': { color: 'info', label: 'In Progress' },
  generated: { color: 'primary', label: 'Generated' },
  finalized: { color: 'success', label: 'Finalized' },
  archived: { color: 'warning', label: 'Archived' },
};

export const getVersionStats = () => ({
  totalVersions: mockVersionHistory.length,
  currentVersion: mockVersionHistory.find((v) => v.status === 'generated')?.version || 0,
  finalizedCount: mockVersionHistory.filter((v) => v.status === 'finalized').length,
  archivedCount: mockVersionHistory.filter((v) => v.status === 'archived').length,
});

export const compareVersions = (versionAId: string, versionBId: string): ComparisonResult | null => {
  const versionA = mockVersionHistory.find((v) => v.id === versionAId);
  const versionB = mockVersionHistory.find((v) => v.id === versionBId);

  if (!versionA || !versionB) return null;

  const getDelta = (a: number, b: number) => b - a;
  const getChange = (a: number, b: number, higherIsBetter: boolean): 'improved' | 'worsened' | 'unchanged' => {
    if (a === b) return 'unchanged';
    return (b > a) === higherIsBetter ? 'improved' : 'worsened';
  };

  const differences = [
    {
      category: 'Coverage',
      metricName: 'Total Lessons',
      valueA: versionA.stats.totalLessons,
      valueB: versionB.stats.totalLessons,
      change: getChange(versionA.stats.totalLessons, versionB.stats.totalLessons, true),
      delta: getDelta(versionA.stats.totalLessons, versionB.stats.totalLessons),
    },
    {
      category: 'Quality',
      metricName: 'Conflicts',
      valueA: versionA.stats.conflictCount,
      valueB: versionB.stats.conflictCount,
      change: getChange(versionA.stats.conflictCount, versionB.stats.conflictCount, false),
      delta: getDelta(versionA.stats.conflictCount, versionB.stats.conflictCount),
    },
    {
      category: 'Quality',
      metricName: 'Hard Rule Violations',
      valueA: versionA.stats.ruleViolations.hard,
      valueB: versionB.stats.ruleViolations.hard,
      change: getChange(versionA.stats.ruleViolations.hard, versionB.stats.ruleViolations.hard, false),
      delta: getDelta(versionA.stats.ruleViolations.hard, versionB.stats.ruleViolations.hard),
    },
    {
      category: 'Quality',
      metricName: 'Soft Rule Violations',
      valueA: versionA.stats.ruleViolations.soft,
      valueB: versionB.stats.ruleViolations.soft,
      change: getChange(versionA.stats.ruleViolations.soft, versionB.stats.ruleViolations.soft, false),
      delta: getDelta(versionA.stats.ruleViolations.soft, versionB.stats.ruleViolations.soft),
    },
    {
      category: 'Coverage',
      metricName: 'Curriculum Coverage',
      valueA: `${versionA.stats.curriculumCoverage}%`,
      valueB: `${versionB.stats.curriculumCoverage}%`,
      change: getChange(versionA.stats.curriculumCoverage, versionB.stats.curriculumCoverage, true),
      delta: getDelta(versionA.stats.curriculumCoverage, versionB.stats.curriculumCoverage),
    },
    {
      category: 'Satisfaction',
      metricName: 'Teacher Preference',
      valueA: `${versionA.stats.teacherPreferenceSatisfaction}%`,
      valueB: `${versionB.stats.teacherPreferenceSatisfaction}%`,
      change: getChange(versionA.stats.teacherPreferenceSatisfaction, versionB.stats.teacherPreferenceSatisfaction, true),
      delta: getDelta(versionA.stats.teacherPreferenceSatisfaction, versionB.stats.teacherPreferenceSatisfaction),
    },
    {
      category: 'Satisfaction',
      metricName: 'Distribution Score',
      valueA: `${versionA.stats.distributionScore}%`,
      valueB: `${versionB.stats.distributionScore}%`,
      change: getChange(versionA.stats.distributionScore, versionB.stats.distributionScore, true),
      delta: getDelta(versionA.stats.distributionScore, versionB.stats.distributionScore),
    },
  ];

  const improvements = differences.filter((d) => d.change === 'improved').length;
  const worsenings = differences.filter((d) => d.change === 'worsened').length;

  return {
    versionA,
    versionB,
    differences,
    summary: {
      overallBetter: improvements > worsenings ? 'B' : worsenings > improvements ? 'A' : 'equal',
      recommendation:
        improvements > worsenings
          ? `Version ${versionB.version} shows overall improvement with ${improvements} improved metrics.`
          : worsenings > improvements
            ? `Version ${versionA.version} is generally better with fewer regressions.`
            : 'Both versions are comparable. Consider specific requirements.',
    },
  };
};

